import React, { useState } from 'react';
import { Phone, Mail, MapPin, Building2, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import useDocumentMeta from '../hooks/useDocumentMeta';

export default function Contact() {
  useDocumentMeta(
    'Kontakt | LIVSLYST ApS',
    'Kontakt LIVSLYST ApS i Brørup. Ring, skriv eller udfyld vores kontaktformular for spørgsmål om visitation, beskyttet beskæftigelse §103 eller rundvisning.'
  );

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '', // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Venligst indtast dit navn.';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Venligst indtast dit telefonnummer.';
    } else if (!/^[0-9\s+()-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Venligst indtast et gyldigt telefonnummer (min. 8 tal).';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Venligst indtast din e-mailadresse.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Venligst indtast en gyldig e-mailadresse.';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Venligst indtast en beskrivelse af din henvendelse.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: '',
          website_url: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Fejl ved indsendelse af formular:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-stone-50 text-stone-800 antialiased min-h-screen overflow-x-hidden">
      {/* Header Section */}
      <section className="bg-stone-100 py-16 md:py-20 border-b border-stone-200">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
            Tag fat i os
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight">
            Kontakt os
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Vi sidder klar til at besvare dine spørgsmål, arrangere en rundvisning eller tage en snak om visitation og pladser.
          </p>
        </motion.div>
      </section>

      {/* Main Contact Content */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Contact Information */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-5 space-y-8"
          >
            <div className="space-y-4">
              <h2 className="text-2xl md:text-3xl font-bold text-stone-900 tracking-tight">
                Lad os tage en snak
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Uanset om du er sagsbehandler, pårørende eller borger, er du altid velkommen til at kontakte os. Vi vægter den personlige kontakt højt og glæder os til at høre fra dig.
              </p>
            </div>

            {/* Info List */}
            <div className="space-y-6 bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-50 rounded-lg text-emerald-800 shrink-0">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-stone-900">Adresse</h4>
                  <p className="text-stone-600 text-sm mt-1 leading-relaxed">
                    Surhavevej 14 A,<br />
                    6650 Brørup
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-50 rounded-lg text-emerald-800 shrink-0">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-stone-900">Telefon</h4>
                  <p className="text-stone-600 text-sm mt-1">
                    <a 
                      href="tel:51504654" 
                      className="hover:text-emerald-800 transition-colors font-semibold text-base whitespace-nowrap"
                    >
                      51 50 46 54
                    </a>
                  </p>
                  <p className="text-stone-400 text-xs mt-0.5">Svarer alle hverdage 08:00 – 16:00</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-50 rounded-lg text-emerald-800 shrink-0">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-stone-900">E-mail</h4>
                  <p className="text-stone-600 text-sm mt-1">
                    <a 
                      href="mailto:Kontakt@livslystaps.dk" 
                      className="hover:text-emerald-800 transition-colors font-semibold text-base whitespace-nowrap"
                    >
                      Kontakt@livslystaps.dk
                    </a>
                  </p>
                  <p className="text-stone-400 text-xs mt-0.5">Vi svarer normalt inden for 24 timer</p>
                </div>
              </div>

              <div className="flex items-start gap-4 border-t border-stone-100 pt-6">
                <div className="p-3 bg-stone-50 rounded-lg text-stone-600 shrink-0">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-stone-900">Virksomhedsoplysninger</h4>
                  <p className="text-stone-500 text-sm mt-1">
                    LIVSLYST ApS<br />
                    CVR-nr: <span className="whitespace-nowrap font-medium">32336302</span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Contact Form */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <div className="bg-white p-8 md:p-10 rounded-2xl border border-stone-200 shadow-sm space-y-6">
              <div className="space-y-2">
                <h3 className="text-xl md:text-2xl font-bold text-stone-900">
                  Send os en besked
                </h3>
                <p className="text-stone-500 text-sm">
                  Udfyld formularen nedenfor, så vender vi tilbage til dig hurtigst muligt.
                </p>
              </div>

              {/* Success State */}
              {submitStatus === 'success' && (
                <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-4 text-emerald-900 animate-fade-in">
                  <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <h4 className="font-bold text-emerald-950">Mange tak for din besked!</h4>
                    <p className="text-sm text-emerald-800 leading-relaxed">
                      Vi har modtaget din henvendelse og vil behandle den med det samme. Du kan forvente at høre fra os inden for 1-2 hverdage.
                    </p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {submitStatus === 'error' && (
                <div className="p-6 bg-red-50 border border-red-200 rounded-xl flex items-start gap-4 text-red-900 animate-fade-in">
                  <AlertCircle className="w-6 h-6 text-red-700 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <h4 className="font-bold text-red-950">Der opstod en fejl</h4>
                    <p className="text-sm text-red-800 leading-relaxed">
                      Kunne ikke sende din besked i øjeblikket. Venligst tjek din internetforbindelse eller prøv igen senere. Du er også altid velkommen til at ringe til os direkte.
                    </p>
                  </div>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot Field (Spam protection) - Absolutely hidden from humans */}
                <div className="sr-only absolute pointer-events-none" aria-hidden="true">
                  <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke)</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Name */}
                <div className="space-y-1.5">
                  <label htmlFor="name" className="block text-sm font-semibold text-stone-700">
                    Navn <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:bg-white transition-all text-sm md:text-base ${
                      errors.name
                        ? 'border-red-300 focus:ring-red-200 focus:border-red-500'
                        : 'border-stone-200 focus:ring-emerald-100 focus:border-emerald-700'
                    }`}
                    placeholder="Dit fulde navn"
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="text-red-600 text-xs font-medium flex items-center gap-1 mt-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>{errors.name}</span>
                    </p>
                  )}
                </div>

                {/* Grid for Phone and Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Phone */}
                  <div className="space-y-1.5">
                    <label htmlFor="phone" className="block text-sm font-semibold text-stone-700">
                      Telefonnummer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:bg-white transition-all text-sm md:text-base ${
                        errors.phone
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-500'
                          : 'border-stone-200 focus:ring-emerald-100 focus:border-emerald-700'
                      }`}
                      placeholder="F.eks. 51504654"
                      disabled={isSubmitting}
                    />
                    {errors.phone && (
                      <p className="text-red-600 text-xs font-medium flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>{errors.phone}</span>
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="space-y-1.5">
                    <label htmlFor="email" className="block text-sm font-semibold text-stone-700">
                      E-mailadresse <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:bg-white transition-all text-sm md:text-base ${
                        errors.email
                          ? 'border-red-300 focus:ring-red-200 focus:border-red-500'
                          : 'border-stone-200 focus:ring-emerald-100 focus:border-emerald-700'
                      }`}
                      placeholder="F.eks. navn@mail.dk"
                      disabled={isSubmitting}
                    />
                    {errors.email && (
                      <p className="text-red-600 text-xs font-medium flex items-center gap-1 mt-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>{errors.email}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-1.5">
                  <label htmlFor="message" className="block text-sm font-semibold text-stone-700">
                    Beskrivelse <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border bg-stone-50 text-stone-900 focus:outline-none focus:ring-2 focus:bg-white transition-all text-sm md:text-base resize-none ${
                      errors.message
                        ? 'border-red-300 focus:ring-red-200 focus:border-red-500'
                        : 'border-stone-200 focus:ring-emerald-100 focus:border-emerald-700'
                    }`}
                    placeholder="Skriv din besked eller beskrivelse af din henvendelse her..."
                    disabled={isSubmitting}
                  />
                  {errors.message && (
                    <p className="text-red-600 text-xs font-medium flex items-center gap-1 mt-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>{errors.message}</span>
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 px-6 bg-emerald-700 hover:bg-emerald-800 disabled:bg-emerald-600/60 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Sender besked...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4.5 h-4.5" />
                      <span>Send besked</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Placeholder Section */}
      <section className="py-12 md:py-16 bg-stone-100 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h3 className="text-xl font-bold text-stone-900">Find os i Brørup</h3>
          <p className="text-stone-600 text-sm max-w-xl mx-auto leading-relaxed">
            Vores faciliteter ligger på Surhavevej 14 A i Brørup. Det er nemt at komme til os i bil eller med offentlig transport fra hele Vejen Kommune og omegn.
          </p>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full h-80 rounded-2xl bg-stone-200 border border-stone-300 shadow-inner flex items-center justify-center relative overflow-hidden"
          >
            {/* Beautiful visual map placeholder */}
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#047857_1px,transparent_1px)] [background-size:16px_16px]" />
            <div className="relative z-10 flex flex-col items-center space-y-2">
              <MapPin className="w-10 h-10 text-emerald-700 animate-bounce" />
              <span className="font-bold text-stone-800">LIVSLYST ApS</span>
              <span className="text-stone-600 text-sm">Surhavevej 14 A, 6650 Brørup</span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
